import { css } from "@compiled/react";

import Skeleton from "@atlaskit/skeleton";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";

import { NotificationGroup as NotificationGroupType } from "../../types";
import { useInfiniteScroll } from "../hooks/use-infinite-scroll";

import NotificationGroup from "./notification-group";

const FeedContainer = css({
    padding: token("space.200")
});

const SentinelStyle = css({
    height: "1px"
});

const EndMessageWrapperStyle = css({
    padding: token("space.200"),
    textAlign: "center",
    color: token("color.text.subtlest"),
    display: "flex",
    flexDirection: "column",
    alignItems: "center"
});

const EndMessageContainerStyle = css({
    width: 225
});

const EndMessageFontStyle = css({
    color: wacToken("color.neutral.light1000"),
    fontSize: "14px",
    fontFamily: "var(--atlassian-sans-font-family)"
});

const LoadingSkeletonContainerStyle = css({
    display: "flex",
    alignItems: "flex-start",
    padding: token("space.100"),
    marginBlockEnd: token("space.200"),
    borderRadius: token("border.radius.100")
});

const LoadingSkeletonAvatarStyle = css({
    marginInlineEnd: token("space.100"),
    flexShrink: 0
});

const LoadingSkeletonTextStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    flex: 1
});
interface GroupedNotifications {
    today: NotificationGroupType[];
    yesterday: NotificationGroupType[];
    thisWeek: NotificationGroupType[];
    thisMonth: NotificationGroupType[];
    older: NotificationGroupType[];
}

interface NotificationFeedProps {
    groupedNotifications: GroupedNotifications;
    onExpand: (groupId: string, continuationToken?: string) => void;
    notificationLogUrl: string;
    loadingGroupId?: string | null;
    onLoadMore?: () => void;
    isLoadingMore?: boolean;
    hasMoreNotifications?: boolean;
}

const renderNotificationGroup = (
    groups: NotificationGroupType[],
    title: string,
    onExpand: (groupId: string, continuationToken?: string) => void,
    notificationLogUrl: string,
    loadingGroupId?: string | null
) => {
    if (groups.length === 0) {
        return null;
    }
    return (
        <NotificationGroup
            title={title}
            notificationGroups={groups}
            onExpand={onExpand}
            notificationLogUrl={notificationLogUrl}
            loadingGroupId={loadingGroupId}
        />
    );
};

const FlagSVG = ({ isEmptyState }: { isEmptyState: boolean }) => (
    <svg
        aria-hidden="true"
        width={isEmptyState ? "172" : "48"}
        height={isEmptyState ? "202" : "41"}
        viewBox="0 0 172 202"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        data-sentry-element="svg"
        data-sentry-component="Flag"
        data-sentry-source-file="main.tsx"
    >
        <g
            clipPath="url(#clip0)"
            data-sentry-element="g"
            data-sentry-source-file="main.tsx"
        >
            <path
                d="M46.2891 10.4251C47.6371 10.7165 48.8141 11.5315 49.5612 12.6907C50.3082 13.85 50.5642 15.2585 50.2728 16.6065L10.2901 201.534L0.125001 199.336L40.1077 14.4087C40.3991 13.0608 41.2141 11.8838 42.3733 11.1367C43.5326 10.3896 44.9411 10.1336 46.2891 10.4251Z"
                fill="#FFC400"
                data-sentry-element="path"
                data-sentry-source-file="main.tsx"
            />
            <path
                d="M147.1 52.4996L128.3 139.4C128.919 135.813 128.117 132.125 126.064 129.12C124.01 126.114 120.867 124.027 117.3 123.3L21.1001 102.5L39.9001 15.5996L136.1 36.3996C139.667 37.1266 142.81 39.2142 144.864 42.2197C146.917 45.2253 147.719 48.9126 147.1 52.4996Z"
                fill="url(#paint0_linear)"
                data-sentry-element="path"
                data-sentry-source-file="main.tsx"
            />
            <path
                d="M171.1 98.3988L152.4 185.099L77.5001 168.999C74.1703 168.267 71.2613 166.255 69.4001 163.399C68.463 161.986 67.8206 160.399 67.5113 158.732C67.202 157.066 67.2322 155.354 67.6001 153.699C69.1001 146.599 76.3001 142.399 83.4001 143.899L112 150.099C113.774 150.479 115.605 150.502 117.388 150.169C119.171 149.835 120.87 149.151 122.386 148.155C123.903 147.16 125.206 145.873 126.221 144.37C127.237 142.867 127.943 141.177 128.3 139.399L138.7 91.3988C138.7 91.3723 138.711 91.3469 138.729 91.3281C138.748 91.3094 138.774 91.2988 138.8 91.2988L171.1 98.3988Z"
                fill="url(#paint1_linear)"
                data-sentry-element="path"
                data-sentry-source-file="main.tsx"
            />
            <path
                d="M128.3 139.4C127.943 141.179 127.236 142.869 126.221 144.372C125.206 145.875 123.903 147.161 122.386 148.157C120.87 149.152 119.171 149.837 117.388 150.17C115.605 150.504 113.774 150.48 112 150.1L82.9001 143.8C79.5587 143.088 76.0712 143.73 73.2029 145.586C70.3345 147.442 68.3196 150.361 67.6001 153.7L76.1001 114.4L117.3 123.3C120.867 124.027 124.01 126.115 126.064 129.121C128.117 132.126 128.919 135.813 128.3 139.4Z"
                fill="#0049B0"
                data-sentry-element="path"
                data-sentry-source-file="main.tsx"
            />
            <path
                d="M47.4001 10.6C50.3272 10.6 52.7001 8.22711 52.7001 5.3C52.7001 2.37289 50.3272 0 47.4001 0C44.473 0 42.1001 2.37289 42.1001 5.3C42.1001 8.22711 44.473 10.6 47.4001 10.6Z"
                fill="#FF991F"
                data-sentry-element="path"
                data-sentry-source-file="main.tsx"
            />
            <path
                d="M88 95.7C87.8568 95.702 87.7175 95.6527 87.6075 95.561C87.4975 95.4693 87.4239 95.3412 87.4 95.2L82.4 75.5C79.4 63.7 82.9 51.4 91.7 42.4C91.7639 42.2767 91.8686 42.1794 91.9963 42.1247C92.1239 42.07 92.2666 42.0612 92.4 42.1C92.4525 42.1 92.5045 42.1103 92.5531 42.1304C92.6016 42.1505 92.6457 42.18 92.6828 42.2171C92.72 42.2543 92.7494 42.2984 92.7695 42.3469C92.7896 42.3954 92.8 42.4475 92.8 42.5C93.2 43.9 107.1 98.3 107.2 98.9C107.221 98.9188 107.238 98.9418 107.25 98.9677C107.262 98.9936 107.268 99.0216 107.268 99.05C107.268 99.0784 107.262 99.1064 107.25 99.1323C107.238 99.1581 107.221 99.1812 107.2 99.2C107.19 99.2903 107.16 99.3772 107.112 99.4542C107.064 99.5312 106.999 99.5962 106.921 99.6443C106.844 99.6925 106.757 99.7225 106.667 99.7322C106.577 99.7418 106.485 99.7308 106.4 99.7L88 95.7ZM55.6 88.7H55.3C55.2265 88.6417 55.1655 88.5694 55.1206 88.4871C55.0757 88.4049 55.0477 88.3144 55.0384 88.2211C55.0291 88.1279 55.0386 88.0337 55.0663 87.9442C55.0941 87.8546 55.1395 87.7716 55.2 87.7L74.1 64C74.1876 63.9018 74.2957 63.8241 74.4167 63.7723C74.5376 63.7205 74.6685 63.6958 74.8 63.7C74.9161 63.7394 75.0215 63.805 75.1082 63.8917C75.1949 63.9784 75.2605 64.0839 75.3 64.2C80.4 72.8 80.2 85.4 74.7 92.4C74.5 92.7 74.3 92.8 74 92.7L55.6 88.7Z"
                fill="#B2D4FF"
                data-sentry-element="path"
                data-sentry-source-file="main.tsx"
            />
        </g>
        <defs data-sentry-element="defs" data-sentry-source-file="main.tsx">
            <linearGradient
                id="paint0_linear"
                x1="28.2014"
                y1="113.474"
                x2="143.095"
                y2="39.4219"
                gradientUnits="userSpaceOnUse"
                data-sentry-element="linearGradient"
                data-sentry-source-file="main.tsx"
            >
                <stop
                    stopColor="#0065FF"
                    data-sentry-element="stop"
                    data-sentry-source-file="main.tsx"
                />
                <stop
                    offset="1"
                    stopColor="#2684FF"
                    data-sentry-element="stop"
                    data-sentry-source-file="main.tsx"
                />
            </linearGradient>
            <linearGradient
                id="paint1_linear"
                x1="67.2901"
                y1="138.209"
                x2="171.14"
                y2="138.209"
                gradientUnits="userSpaceOnUse"
                data-sentry-element="linearGradient"
                data-sentry-source-file="main.tsx"
            >
                <stop
                    stopColor="#0052CC"
                    data-sentry-element="stop"
                    data-sentry-source-file="main.tsx"
                />
                <stop
                    offset="1"
                    stopColor="#0065FF"
                    data-sentry-element="stop"
                    data-sentry-source-file="main.tsx"
                />
            </linearGradient>
            <clipPath
                id="clip0"
                data-sentry-element="clipPath"
                data-sentry-source-file="main.tsx"
            >
                <path
                    d="M0.100098 0H171.1V201.5H0.100098V0Z"
                    fill="white"
                    data-sentry-element="path"
                    data-sentry-source-file="main.tsx"
                />
            </clipPath>
        </defs>
    </svg>
);

const NotificationFeed = ({
    groupedNotifications,
    onExpand,
    notificationLogUrl,
    loadingGroupId,
    onLoadMore,
    isLoadingMore = false,
    hasMoreNotifications = true
}: NotificationFeedProps) => {
    const { today, yesterday, thisWeek, thisMonth, older } =
        groupedNotifications;

    const handleLoadMore =
        onLoadMore ||
        (() => {
            // Default no-op handler if onLoadMore is not provided
        });

    const sentinelRef = useInfiniteScroll({
        onLoadMore: handleLoadMore,
        isLoading: isLoadingMore,
        hasMore: hasMoreNotifications
    });

    const hasAnyNotifications =
        today.length > 0 ||
        yesterday.length > 0 ||
        thisWeek.length > 0 ||
        thisMonth.length > 0 ||
        older.length > 0;

    const isEmptyState = !hasAnyNotifications;

    return (
        <div css={FeedContainer}>
            {isEmptyState ? (
                <div css={EndMessageWrapperStyle}>
                    <FlagSVG isEmptyState={true} />
                    <div css={EndMessageContainerStyle}>
                        <p className="body-sm" css={EndMessageFontStyle}>
                            You have no notifications from the last 30 days.
                        </p>
                    </div>
                </div>
            ) : (
                <>
                    {renderNotificationGroup(
                        today,
                        "Today",
                        onExpand,
                        notificationLogUrl,
                        loadingGroupId
                    )}
                    {renderNotificationGroup(
                        yesterday,
                        "Yesterday",
                        onExpand,
                        notificationLogUrl,
                        loadingGroupId
                    )}
                    {renderNotificationGroup(
                        thisWeek,
                        "This Week",
                        onExpand,
                        notificationLogUrl,
                        loadingGroupId
                    )}
                    {renderNotificationGroup(
                        thisMonth,
                        "This Month",
                        onExpand,
                        notificationLogUrl,
                        loadingGroupId
                    )}
                    {renderNotificationGroup(
                        older,
                        "Older",
                        onExpand,
                        notificationLogUrl,
                        loadingGroupId
                    )}
                    {isLoadingMore && (
                        <div css={LoadingSkeletonContainerStyle}>
                            <div css={LoadingSkeletonAvatarStyle}>
                                <Skeleton width="44px" height="44px" />
                            </div>
                            <div css={LoadingSkeletonTextStyle}>
                                <Skeleton width="50%" height="12px" />
                                <Skeleton width="70%" height="12px" />
                            </div>
                        </div>
                    )}
                    <div ref={sentinelRef} css={SentinelStyle} />
                    {!hasMoreNotifications && (
                        <div css={EndMessageWrapperStyle}>
                            <FlagSVG isEmptyState={false} />
                            <div css={EndMessageContainerStyle}>
                                <p
                                    className="body-sm"
                                    css={EndMessageFontStyle}
                                >
                                    That's all your notifications from the last
                                    30 days.
                                </p>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default NotificationFeed;
